import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  Upload,
  FileText,
  Calendar,
  Clock,
  User,
  BookOpen,
  GraduationCap,
  Loader,
  CheckCircle,
  AlertTriangle,
  Zap,
  X
} from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import { CreateExamRequest } from '../types/exam.types';
import { examApi } from '../services/examApi';
import { coursesApi, Course } from '../services/api';
import { analytics } from '../services/analytics';
import { MixedText } from '../utils/rtl';
import { CourseSelector } from './quick-actions/shared/CourseSelector';
import { Card, CardHeader, CardTitle, CardContent, CardFooter, SpaceCard } from './ui/Card';
import { Button } from './ui/Button';
import { LoadingSpinner } from './ui/LoadingSpinner';
import { DashboardContainer } from './layout/PageContainer';

const ExamCreationView: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'he';
  const { courseId } = useParams<{ courseId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Check if navigated from dashboard
  const fromDashboard = searchParams.get('source') === 'dashboard';

  // Determine if we have a course from URL params or search params
  const urlCourseId = courseId ? parseInt(courseId) : null;
  const searchCourseId = searchParams.get('course_id') ? parseInt(searchParams.get('course_id')!) : null;
  const hasFixedCourse = urlCourseId !== null;

  // Course selection state
  const [courses, setCourses] = useState<Course[]>([]);
  const [coursesLoading, setCoursesLoading] = useState(!hasFixedCourse);
  const [selectedCourseId, setSelectedCourseId] = useState<number | null>(urlCourseId || searchCourseId);
  const [newCourseName, setNewCourseName] = useState('');
  const [isCreatingNewCourse, setIsCreatingNewCourse] = useState(false);

  // Form state
  const [examTitle, setExamTitle] = useState('');
  const [customInstructions, setCustomInstructions] = useState('');

  // File state
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  // Course materials state
  const [courseMaterialFiles, setCourseMaterialFiles] = useState<File[]>([]);
  const [uploadingMaterials, setUploadingMaterials] = useState(false);
  const [materialUploadError, setMaterialUploadError] = useState<string | null>(null);

  // UI states
  const [creating, setCreating] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [processingComplete, setProcessingComplete] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<{[key: string]: string}>({});
  const [currentExamId, setCurrentExamId] = useState<number | null>(null);
  const [finalCourseId, setFinalCourseId] = useState<number | null>(null);

  // Get the final course ID to use (either from URL or user selection)
  const getFinalCourseId = (): number | null => {
    // If we have a stored final course ID (after creation), use it
    if (finalCourseId) return finalCourseId;
    if (hasFixedCourse) return urlCourseId;
    if (isCreatingNewCourse) return null; // Will be set after course creation
    return selectedCourseId;
  };

  // Initialize finalCourseId if we have a fixed course
  useEffect(() => {
    if (hasFixedCourse && urlCourseId) {
      setFinalCourseId(urlCourseId);
    } else if (!hasFixedCourse && searchCourseId) {
      setFinalCourseId(searchCourseId);
    }
  }, [hasFixedCourse, urlCourseId, searchCourseId]);

  // Update finalCourseId when user selects a different course
  useEffect(() => {
    if (!hasFixedCourse && !isCreatingNewCourse && selectedCourseId) {
      setFinalCourseId(selectedCourseId);
    } else if (!hasFixedCourse && isCreatingNewCourse) {
      // Clear finalCourseId when creating a new course (it will be set after creation)
      setFinalCourseId(null);
    }
  }, [hasFixedCourse, isCreatingNewCourse, selectedCourseId]);

  // Load courses if not in fixed course mode
  useEffect(() => {
    if (!hasFixedCourse) {
      loadCourses();
    }
  }, [hasFixedCourse]);

  const loadCourses = async () => {
    try {
      setCoursesLoading(true);
      const coursesData = await coursesApi.getAllCourses();
      setCourses(coursesData);
    } catch (err) {
      setError(t('sessions:create.errors.load_courses_failed'));
      console.error('Error loading courses:', err);
    } finally {
      setCoursesLoading(false);
    }
  };

  // Poll exam status when processing starts
  useEffect(() => {
    let interval: NodeJS.Timeout;
    
    if (processing && currentExamId) {
      interval = setInterval(async () => {
        try {
          const exam = await examApi.getExam(currentExamId);
          if (exam.processing_status === 'completed') {
            setProcessingComplete(true);
            setProcessing(false);
            clearInterval(interval);
          } else if (exam.processing_status === 'failed') {
            setError(t('common:errors.processing_failed'));
            setProcessing(false);
            setSuccess(false);
            clearInterval(interval);
          }
        } catch (err) {
          console.error('Error checking exam status:', err);
          // Continue polling - might be a temporary network issue
        }
      }, 5000); // Poll every 5 seconds
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [processing, currentExamId, t]);

  const handleTitleChange = (value: string) => {
    setExamTitle(value);
    
    // Clear validation error for title
    if (validationErrors.title) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.title;
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    const errors: {[key: string]: string} = {};

    if (!examTitle.trim()) {
      errors.title = t('exams:examCreation.validation.titleRequired');
    }

    if (!selectedFile) {
      errors.file = t('exams:examCreation.validation.fileRequired');
    } else {
      // Validate file type
      const allowedTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/msword'];
      if (!allowedTypes.includes(selectedFile.type)) {
        errors.file = t('exams:examCreation.validation.invalidFileType');
      }
      
      // Validate file size (50MB limit)
      const maxSize = 50 * 1024 * 1024; // 50MB in bytes
      if (selectedFile.size > maxSize) {
        errors.file = t('exams:examCreation.validation.fileTooLarge');
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const onDrop = (acceptedFiles: File[], rejectedFiles: any[]) => {
    if (rejectedFiles.length > 0) {
      setError(t('exams:examCreation.validation.invalidFileType'));
      return;
    }

    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      setSelectedFile(file);
      setError(null);

      // Clear file validation error
      if (validationErrors.file) {
        setValidationErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors.file;
          return newErrors;
        });
      }
    }
  };

  const onCourseMaterialsDrop = (acceptedFiles: File[], rejectedFiles: any[]) => {
    if (rejectedFiles.length > 0) {
      setMaterialUploadError(t('exams:examCreation.validation.invalidFileType'));
      return;
    }

    if (acceptedFiles.length > 0) {
      setCourseMaterialFiles(prev => [...prev, ...acceptedFiles]);
      setMaterialUploadError(null);
    }
  };

  const removeMaterialFile = (index: number) => {
    setCourseMaterialFiles(prev => prev.filter((_, i) => i !== index));
  };

  const uploadCourseMaterials = async (courseId: number) => {
    if (courseMaterialFiles.length === 0) return;

    setUploadingMaterials(true);
    setMaterialUploadError(null);

    const uploadPromises = courseMaterialFiles.map(file =>
      examApi.uploadCourseMaterial(courseId, file, 'reference')
    );

    try {
      await Promise.all(uploadPromises);

      analytics.track('Course Materials Uploaded from Exam Creation', {
        course_id: courseId,
        file_count: courseMaterialFiles.length,
        source: 'exam_creation'
      });
    } catch (err) {
      console.error('Error uploading course materials:', err);
      setMaterialUploadError(t('exams:courseMaterials.uploadError'));
      analytics.trackError('Course Materials Upload Failed', {
        courseId,
        fileCount: courseMaterialFiles.length,
        source: 'exam_creation'
      });
    } finally {
      setUploadingMaterials(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setCreating(true);
    setError(null);

    try {
      let finalCourseId = getFinalCourseId();
      
      // Create new course if needed
      if (!hasFixedCourse && isCreatingNewCourse) {
        const newCourse = await coursesApi.createCourse({
          name: newCourseName.trim(),
          description: ``
        });
        
        finalCourseId = newCourse.id;
        
        // Track course creation
        analytics.track('Course Created from Quick Action', {
          course_id: newCourse.id,
          course_name: newCourseName.trim(),
          source: 'exam_creation'
        });
      }

      if (!finalCourseId) {
        throw new Error('No course selected');
      }

      // Store the final course ID for navigation
      setFinalCourseId(finalCourseId);

      // Create simplified exam data
      const examData: CreateExamRequest = {
        title: examTitle.trim(),
        description: '',
        exam_type: 'practice',
        academic_year: '',
        semester: '',
        professor_name: '',
        exam_date: '',
        duration_minutes: undefined,
        total_points: undefined,
        custom_instructions: customInstructions.trim() || undefined
      };

      // Create exam with file
      const response = await examApi.createExam(finalCourseId, examData, selectedFile!);
      
      analytics.track('Exam Created', {
        course_id: finalCourseId,
        exam_id: response.id,
        exam_title: examTitle,
        exam_type: 'practice',
        has_file: !!selectedFile,
        file_size: selectedFile?.size,
        source: hasFixedCourse ? 'course_view' : 'quick_action'
      });

      // Upload course materials if any (do this before processing to avoid credit issues)
      if (courseMaterialFiles.length > 0) {
        await uploadCourseMaterials(finalCourseId);
      }

      // Start processing the exam and wait for it to avoid credit errors after success state
      setProcessing(true);
      setCurrentExamId(response.id);

      try {
        await examApi.processExam(response.id, customInstructions.trim() || undefined);

        analytics.track('Exam Processing Started', {
          course_id: finalCourseId,
          exam_id: response.id
        });

        // Show success message only if processing started successfully
        setSuccess(true);
      } catch (processErr: any) {
        // If processing fails with credit error, reset states and let error bubble up
        if (processErr?.name === 'CreditErrorHandled') {
          setProcessing(false);
          setCurrentExamId(null);
          throw processErr; // Re-throw to be caught by main catch block
        }
        
        // For other processing errors, still show success since exam was created
        console.warn('Exam created but processing failed:', processErr);
        setSuccess(true);
      }
    } catch (err: any) {
      console.error('Error creating exam:', err);
      
      // Skip handling if credit error was already handled by interceptor
      if (err?.name === 'CreditErrorHandled') {
        // Reset any processing states that may have been set
        setProcessing(false);
        setCurrentExamId(null);
        setSuccess(false);
        return;
      }
      
      setError(t('common:errors.create_failed'));
      analytics.trackError('Exam Creation Failed', {
        courseId: getFinalCourseId(),
        examTitle: examTitle,
        examType: 'practice',
        isCreatingNewCourse: !hasFixedCourse && isCreatingNewCourse
      });
    } finally {
      setCreating(false);
      // Don't set processing to false here - let the polling handle it
    }
  };

  const formatFileSize = (bytes: number): string => {
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(1)} MB`;
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/msword': ['.doc']
    },
    maxFiles: 1,
    disabled: creating
  });

  const { getRootProps: getCourseMaterialsRootProps, getInputProps: getCourseMaterialsInputProps, isDragActive: isCourseMaterialsDragActive } = useDropzone({
    onDrop: onCourseMaterialsDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/msword': ['.doc']
    },
    disabled: creating || uploadingMaterials
  });

  return (
    <DashboardContainer>
      <div className="space-y-8">
        {/* Back Button */}
        <button
          onClick={() => {
            const backCourseId = getFinalCourseId();
            if (fromDashboard || (!hasFixedCourse && !backCourseId)) {
              navigate('/');
            } else if (hasFixedCourse && backCourseId) {
              navigate(`/course/${backCourseId}/exams`);
            } else {
              navigate('/');
            }
          }}
          className={`flex items-center text-bg-secondary hover:text-vibrant-orange transition-colors group font-hebrew ${isRTL ? 'space-x-reverse space-x-2' : 'space-x-2'}`}
        >
          {isRTL ? (
            <ArrowLeft className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200 ml-3 mr-3" />
          ) : (
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform duration-200" />
          )}
          <span className={`font-medium ${isRTL ? 'text-right' : ''}`}>
            <MixedText>{fromDashboard ? t('common:actions.back_to_dashboard') : t('common:actions.back_to_exams')}</MixedText>
          </span>
        </button>

        {/* Page Header */}
        <SpaceCard constellation="cassiopeia" className="group hover:shadow-xl transition-all duration-300">
          <CardHeader>
            <div className={`flex items-center ${isRTL ? 'flex-row-reverse space-x-reverse space-x-4' : 'space-x-4'}`}>
              <div className="w-16 h-16 bg-gradient-to-br from-vibrant-orange/10 to-vibrant-orange/20 rounded-2xl flex items-center justify-center">
                <GraduationCap className="w-8 h-8 text-vibrant-orange" />
              </div>
              <div className={`flex-1 ${isRTL ? 'text-right' : ''}`}>
                <CardTitle className={`text-2xl md:text-3xl mb-2 font-hebrew ${isRTL ? 'text-right' : ''}`}>
                  <MixedText>{t('exams:examCreation.title')}</MixedText>
                </CardTitle>
                <CardContent className={`text-surface-secondary font-hebrew ${isRTL ? 'text-right' : ''}`}>
                  {t('exams:examCreation.metadata')}
                </CardContent>
              </div>
            </div>
          </CardHeader>
        </SpaceCard>

        {/* Error Display */}
        {error && (
          <Card className="border-vibrant-orange/30 bg-vibrant-orange/10 backdrop-blur-sm">
            <CardContent>
              <div className="flex items-center">
                <AlertTriangle className="w-5 h-5 text-vibrant-orange mr-3" />
                <p className={`text-vibrant-orange font-hebrew text-center`}>⚠️ {error}</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Success Display */}
        {success && (
          <SpaceCard constellation="orion" className="group hover:shadow-xl transition-all duration-300">
            <CardContent className="text-center space-y-4 py-4 md:py-8">
              <div className="w-16 h-16 mx-auto">
                <img 
                  src="/astronaut_cool.png" 
                  alt="Success" 
                  className="w-full h-full object-contain"
                />
              </div>
              <div>
                <CardTitle className="text-xl font-bold mb-2 font-hebrew text-center">
                  <MixedText>{t('exams:examCreation.success.title')}</MixedText>
                </CardTitle>
                <CardContent className="text-surface-secondary font-hebrew mb-4 text-center">
                  <MixedText>{t('exams:examCreation.success.description')}</MixedText>
                </CardContent>
                {!processingComplete && (
                  <p className="text-surface-muted font-hebrew text-sm text-center">
                    <MixedText>{t('exams:examCreation.success.timeEstimate')}</MixedText>
                  </p>
                )}
              </div>
              <div className="flex justify-center pt-2 md:pt-4">
                {processingComplete ? (
                  <Button
                    onClick={() => {
                      const backCourseId = getFinalCourseId();
                      if (currentExamId && backCourseId) {
                        navigate(`/course/${backCourseId}/exams/${currentExamId}/practice`);
                      } else if (hasFixedCourse && backCourseId) {
                        navigate(`/course/${backCourseId}/exams`);
                      } else {
                        navigate('/');
                      }
                    }}
                    variant="primary"
                    className="px-6 py-3 flex items-center justify-center"
                  >
                    <MixedText>{t('exams:examCreation.success.gotToSolution')}</MixedText>
                  </Button>
                ) : (
                  <div className="flex items-center justify-center px-6 py-3 bg-muted-purple/10 border border-muted-purple/30 rounded-lg">
                    <LoadingSpinner 
                      variant="simple" 
                      size="sm" 
                      color="purple" 
                      className="mr-3" 
                    />
                    <span className="text-muted-purple font-hebrew">
                      <MixedText>{t('exams:examCreation.processing')}</MixedText>
                    </span>
                  </div>
                )}
              </div>
            </CardContent>
          </SpaceCard>
        )}

        {!success && (
          <form onSubmit={handleSubmit} className="space-y-4 md:space-y-8">
            {/* Exam Creation Form */}
            <SpaceCard constellation="ursa" className="group hover:shadow-xl transition-all duration-300">
              <CardContent>
                <div className="max-w-2xl mx-auto space-y-4 md:space-y-8">
                  {/* Course Selection - only show if not in fixed course mode */}
                  {!hasFixedCourse && (
                    <div className="space-y-2">
                      <CourseSelector
                        selectedCourseId={selectedCourseId}
                        newCourseName={newCourseName}
                        onCourseSelect={setSelectedCourseId}
                        onNewCourseNameChange={setNewCourseName}
                        isCreatingNew={isCreatingNewCourse}
                        onToggleCreateNew={() => setIsCreatingNewCourse(!isCreatingNewCourse)}
                        existingCourses={courses}
                        isRTL={isRTL}
                        loading={coursesLoading || creating}
                        disabled={creating}
                      />
                      {validationErrors.course && (
                        <p className={`text-vibrant-orange text-sm mt-2 font-hebrew ${isRTL ? 'text-right' : ''}`}>
                          {validationErrors.course}
                        </p>
                      )}
                    </div>
                  )}
                  {/* Exam Name */}
                  <div>
                    <label className={`block text-lg font-semibold text-surface-primary mb-3 font-hebrew ${isRTL ? 'text-right' : ''}`}>
                      📚 {t('exams:examCreation.form.title')} *
                    </label>
                    <input
                      type="text"
                      value={examTitle}
                      onChange={(e) => handleTitleChange(e.target.value)}
                      placeholder={t('exams:examCreation.form.titlePlaceholder')}
                      className={`w-full px-4 py-3 text-lg border-2 border-surface-muted/30 rounded-xl focus:outline-none focus:ring-2 focus:ring-vibrant-orange/20 focus:border-vibrant-orange transition-all duration-200 ${isRTL ? 'text-right' : ''} ${validationErrors.title ? 'border-vibrant-orange/50 bg-vibrant-orange/5' : ''}`}
                      dir={isRTL ? 'rtl' : 'ltr'}
                    />
                    {validationErrors.title && (
                      <p className={`text-vibrant-orange text-sm mt-2 font-hebrew ${isRTL ? 'text-right' : ''}`}>
                        {validationErrors.title}
                      </p>
                    )}
                  </div>

                  {/* Custom Instructions */}
                  <div>
                    <label className={`block text-lg font-semibold text-surface-primary mb-3 font-hebrew ${isRTL ? 'text-right' : ''}`}>
                      {t('exams:examCreation.form.customInstructions')} 🧠
                    </label>
                    <p className={`text-sm text-surface-secondary mb-3 font-hebrew ${isRTL ? 'text-right' : ''}`}>
                      <MixedText>{t('exams:examCreation.form.customInstructionsDescription')}</MixedText>
                    </p>
                    <textarea
                      value={customInstructions}
                      onChange={(e) => setCustomInstructions(e.target.value)}
                      placeholder={t('exams:examCreation.form.customInstructionsPlaceholder')}
                      rows={3}
                      className={`w-full px-4 py-3 text-lg border-2 border-surface-muted/30 rounded-xl focus:outline-none focus:ring-2 focus:ring-vibrant-orange/20 focus:border-vibrant-orange transition-all duration-200 resize-none bg-surface-muted/5 ${isRTL ? 'text-right' : ''}`}
                      dir={isRTL ? 'rtl' : 'ltr'}
                    />
                    <p className={`text-xs text-surface-muted mt-2 font-hebrew ${isRTL ? 'text-right' : ''}`}>
                      💡 <MixedText>{t('exams:examCreation.form.customInstructionsHint')}</MixedText>
                    </p>
                  </div>

                  {/* File Upload */}
                  <div>
                    <label className={`block text-lg font-semibold text-surface-primary mb-3 font-hebrew ${isRTL ? 'text-right' : ''}`}>
                      📁 {t('exams:examCreation.uploadFile')} *
                    </label>
                    <div
                      {...getRootProps()}
                      className={`border-2 border-dashed rounded-xl p-4 md:p-8 lg:p-12 text-center transition-all duration-200 ${
                        isDragActive
                          ? 'border-vibrant-orange bg-vibrant-orange/10 scale-105'
                          : creating
                          ? 'border-surface-muted/30 bg-surface-muted/10 cursor-not-allowed'
                          : 'border-surface-muted/30 hover:border-vibrant-orange hover:bg-vibrant-orange/10 cursor-pointer'
                      } ${validationErrors.file ? 'border-vibrant-orange/50 bg-vibrant-orange/5' : ''}`}
                    >
                      <input {...getInputProps()} />
                      <div className="space-y-4">
                        {selectedFile ? (
                          <>
                            <CheckCircle className="w-12 h-12 md:w-16 md:h-16 text-vibrant-orange mx-auto" />
                            <div className="text-center">
                              <p className="text-vibrant-orange font-semibold text-lg font-hebrew">
                                ✅ <MixedText>{selectedFile.name}</MixedText>
                              </p>
                              <p className="text-vibrant-orange/80 mt-1 font-hebrew">
                                <MixedText>
                                  {`${formatFileSize(selectedFile.size)} • ${selectedFile.type.includes('pdf') ? 'PDF' : 'DOCX'}`}
                                </MixedText>
                              </p>
                            </div>
                          </>
                        ) : (
                          <>
                            <Upload className="w-12 h-12 md:w-16 md:h-16 text-surface-muted mx-auto" />
                            <div className="text-center">
                              <p className="text-surface-primary font-semibold text-lg font-hebrew">
                                {isDragActive ? (
                                  <span>🛸 {t('common:upload.drop_files_here')}</span>
                                ) : (
                                  <span>📁 {t('exams:examCreation.dragDropFile')}</span>
                                )}
                              </p>
                              <p className="text-surface-secondary mt-2 font-hebrew">
                                <span>{`PDF, DOCX • ${t('common:upload.max_size', { size: '50MB' })}`}</span>
                              </p>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                    {validationErrors.file && (
                      <p className={`text-vibrant-orange text-sm mt-2 font-hebrew ${isRTL ? 'text-right' : ''}`}>
                        {validationErrors.file}
                      </p>
                    )}
                  </div>

                  {/* Course Materials Upload (Optional) */}
                  <div>
                    <label className={`block text-lg font-semibold text-surface-primary mb-3 font-hebrew ${isRTL ? 'text-right' : ''}`}>
                      📚 {t('exams:courseMaterials.upload.title', 'Upload Course Materials')} ({t('common:labels.optional', 'Optional')})
                    </label>
                    <p className={`text-sm text-surface-secondary mb-3 font-hebrew ${isRTL ? 'text-right' : ''}`}>
                      <MixedText>{t('exams:courseMaterials.upload.description', 'Upload additional reference materials that can be used for future exams in this course.')}</MixedText>
                    </p>
                    <div
                      {...getCourseMaterialsRootProps()}
                      className={`border-2 border-dashed rounded-xl p-6 text-center transition-all duration-200 cursor-pointer ${
                        isCourseMaterialsDragActive
                          ? 'border-muted-purple bg-muted-purple/10 scale-105'
                          : creating || uploadingMaterials
                          ? 'border-surface-muted/30 bg-surface-muted/10 cursor-not-allowed'
                          : 'border-surface-muted/30 hover:border-muted-purple hover:bg-muted-purple/10'
                      }`}
                    >
                      <input {...getCourseMaterialsInputProps()} />
                      {uploadingMaterials ? (
                        <div className="space-y-3">
                          <Loader className="w-8 h-8 text-muted-purple mx-auto animate-spin" />
                          <p className="text-muted-purple font-medium text-center">
                            <MixedText>{t('common:messages.uploading')}</MixedText>
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <Upload className={`w-8 h-8 mx-auto ${
                            isCourseMaterialsDragActive ? 'text-muted-purple' : 'text-surface-muted'
                          }`} />
                          <div>
                            <p className="text-surface-primary font-medium text-center">
                              <MixedText>
                                {isCourseMaterialsDragActive
                                  ? t('common:upload.dropFiles')
                                  : t('exams:courseMaterials.dragDrop', 'Drag & drop materials here or click to browse')
                                }
                              </MixedText>
                            </p>
                            <p className="text-sm text-surface-secondary mt-1 text-center">
                              <MixedText>{t('common:upload.supportedFormats', 'PDF, DOCX')}</MixedText>
                            </p>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Uploaded Course Materials List */}
                    {courseMaterialFiles.length > 0 && (
                      <div className="mt-4 space-y-2">
                        <p className={`text-sm font-medium text-surface-primary font-hebrew ${isRTL ? 'text-right' : ''}`}>
                          📎 {t('exams:courseMaterials.uploadedFiles', 'Uploaded files')}: {courseMaterialFiles.length}
                        </p>
                        <div className="space-y-2">
                          {courseMaterialFiles.map((file, index) => (
                            <div
                              key={index}
                              className={`flex items-center justify-between p-2 bg-surface-muted/10 rounded-lg ${isRTL ? 'flex-row-reverse' : ''}`}
                            >
                              <div className={`flex items-center ${isRTL ? 'flex-row-reverse space-x-reverse space-x-2' : 'space-x-2'}`}>
                                <FileText className="w-4 h-4 text-muted-purple" />
                                <span className="text-sm text-surface-primary">{file.name}</span>
                                <span className="text-xs text-surface-secondary">
                                  ({formatFileSize(file.size)})
                                </span>
                              </div>
                              <button
                                onClick={() => removeMaterialFile(index)}
                                className="p-1 hover:bg-vibrant-orange/10 rounded transition-colors"
                                disabled={creating || uploadingMaterials}
                              >
                                <X className="w-4 h-4 text-surface-secondary hover:text-vibrant-orange" />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {materialUploadError && (
                      <div className="mt-4 p-3 bg-vibrant-orange/10 border border-vibrant-orange/30 rounded-lg">
                        <p className={`text-vibrant-orange text-sm ${isRTL ? 'text-right' : ''}`}>
                          <MixedText>{materialUploadError}</MixedText>
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <CardFooter className="border-t border-surface-muted/20 pt-4 md:pt-6">
                    <div className="flex flex-col gap-3 w-full">
                      {/* Availability Notice */}
                      <div className={`text-center text-sm text-surface-muted font-hebrew ${isRTL ? 'text-right' : ''}`}>
                        <span className="inline-flex items-center">
                          💡 <MixedText>{t('exams:examCreation.form.availability_notice')}</MixedText>
                        </span>
                      </div>

                      <Button
                        type="submit"
                        disabled={creating || processing}
                        variant="primary"
                        size="lg"
                        className="w-full py-3 md:py-4 text-base md:text-lg flex items-center justify-center"
                      >
                        {creating || processing ? (
                          <>
                            <LoadingSpinner 
                              variant={processing ? "satellite" : "rocket"} 
                              size="sm" 
                              color="primary" 
                              className="mr-2" 
                            />
                            <span>
                              {processing 
                                ? `${t('exams:examCreation.processing')}`
                                : `${t('common:messages.creating')}`
                              }
                            </span>
                          </>
                        ) : (
                          <>
                            <MixedText>{` ${t('exams:examCreation.createExam') }`}</MixedText>
                          </>
                        )}
                      </Button>

                      <Button
                        type="button"
                        onClick={() => {
                          const backCourseId = getFinalCourseId();
                          if (fromDashboard || (!hasFixedCourse && !backCourseId)) {
                            navigate('/');
                          } else if (hasFixedCourse && backCourseId) {
                            navigate(`/course/${backCourseId}/exams`);
                          } else {
                            navigate('/');
                          }
                        }}
                        variant="secondary"
                        size="lg"
                        className="w-full py-3"
                      >
                        <MixedText>{t('common:buttons.cancel')}</MixedText>
                      </Button>
                    </div>
                  </CardFooter>
                </div>
              </CardContent>
            </SpaceCard>
          </form>
        )}
      </div>
    </DashboardContainer>
  );
};

export default ExamCreationView; 